import React, { useMemo } from "react";
import { usePersonalization } from "@/components/personalization/PersonalizationContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Premium gold-on-dark badge showing the user's display name, archetype, and
 * normalized tier. Sits next to the existing header chip; strictly additive.
 */
export default function OracleUserProfileBadge({
  testid = "oracle-user-profile-badge",
  onOpenSettings,
} = {}) {
  const { profile, relationship, isAuthed } = usePersonalization();
  const auth = useAuth();
  const displayName = useMemo(() => {
    if (profile?.display_name) return profile.display_name;
    const u = auth?.user;
    if (u?.name) return u.name;
    if (u?.email) return u.email.split("@")[0];
    return null;
  }, [profile, auth]);

  if (!isAuthed || !displayName) return null;

  const archetype = profile?.archetype || relationship?.archetype || "Member";
  const tier = (profile?.membership_tier || auth?.user?.tier || "free").toString().toUpperCase().replace(/_/g, " ");
  const firstName = displayName.split(" ")[0];

  return (
    <button
      type="button"
      onClick={onOpenSettings}
      data-testid={testid}
      aria-label={`Personalization settings for ${displayName}`}
      style={S.btn}
    >
      <span aria-hidden="true" style={S.dot} />
      <span style={S.name}>{firstName}</span>
      <span style={S.divider}>·</span>
      <span style={S.archetype} data-testid={`${testid}-archetype`}>{archetype}</span>
      <span style={S.divider}>·</span>
      <span style={S.tier} data-testid={`${testid}-tier`}>{tier}</span>
    </button>
  );
}

const S = {
  btn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", background: "linear-gradient(135deg, rgba(11,15,20,0.92), rgba(8,32,22,0.92))", border: "1px solid rgba(197,160,89,0.45)", borderRadius: 999, color: "#fde68a", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.08em", fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 16px rgba(0,0,0,0.35)" },
  dot: { width: 8, height: 8, borderRadius: "50%", background: "#10b981", boxShadow: "0 0 10px #10b981" },
  name: { color: "#fde68a" },
  divider: { color: "#64748b" },
  archetype: { color: "#c5a059", textTransform: "uppercase" },
  tier: { color: "#22d3ee" },
};
